import { useState, useEffect, useCallback } from 'react';
import { getOpenInitiatives, getParticipatedInitiatives } from '@/lib/api/initiatives';
import type { Initiative } from '@/types/initiative';

export interface UseOpenInitiativesReturn {
  openInitiatives: Initiative[];
  participatedInitiatives: Initiative[];
  openLoading: boolean;
  participatedLoading: boolean;
  error: string | null;
  refetchOpen: () => Promise<void>;
  refetchParticipated: () => Promise<void>;
}

export function useOpenInitiatives(enabled = false): UseOpenInitiativesReturn {
  const [openInitiatives, setOpenInitiatives] = useState<Initiative[]>([]);
  const [participatedInitiatives, setParticipatedInitiatives] = useState<Initiative[]>([]);
  const [openLoading, setOpenLoading] = useState(false);
  const [participatedLoading, setParticipatedLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOpen = useCallback(async () => {
    try {
      setOpenLoading(true);
      const res = await getOpenInitiatives();
      const data = res.success && res.data && Array.isArray(res.data) ? res.data : [];
      setOpenInitiatives(data);
    } catch {
      setError('Failed to load open initiatives');
      setOpenInitiatives([]);
    } finally {
      setOpenLoading(false);
    }
  }, []);

  const fetchParticipated = useCallback(async () => {
    try {
      setParticipatedLoading(true);
      const res = await getParticipatedInitiatives();
      const data = res.success && res.data && Array.isArray(res.data) ? res.data : [];
      setParticipatedInitiatives(data);
    } catch {
      setError('Failed to load participated initiatives');
      setParticipatedInitiatives([]);
    } finally {
      setParticipatedLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    fetchOpen();
    fetchParticipated();
  }, [enabled, fetchOpen, fetchParticipated]);

  return {
    openInitiatives,
    participatedInitiatives,
    openLoading,
    participatedLoading,
    error,
    refetchOpen: fetchOpen,
    refetchParticipated: fetchParticipated,
  };
}
